import { supabase } from '@/integrations/supabase/client';

/**
 * Turns a finished workflow run's records into real saved charts that are
 * tagged with the run they came from, so "Open dashboard" can scope to it.
 */
export const saveWorkflowCharts = async (
  userId: string,
  runId: string,
  runVersion: number,
  records: Record<string, any>[],
): Promise<string[]> => {
  if (!records.length) return [];

  const sourceName = `Workflow run v${runVersion}`;
  const charts: any[] = [];
  const sample = records[0];

  // 1. Lead score distribution (only when predictions produced scores)
  const scored = records.filter(r => typeof r._lead_score === 'number');
  if (scored.length) {
    const buckets = [
      { name: '0-25', min: 0, max: 25 },
      { name: '26-50', min: 26, max: 50 },
      { name: '51-75', min: 51, max: 75 },
      { name: '76-100', min: 76, max: 100 },
    ].map(b => ({
      name: b.name,
      value: scored.filter(r => r._lead_score >= b.min && r._lead_score <= b.max).length,
    }));

    charts.push({
      title: 'Predicted score distribution',
      chart_type: 'bar',
      x_field: 'name',
      y_field: 'value',
      aggregation: 'count',
      data: buckets,
      config: { runId, runVersion },
    });
  }

  // 2. Breakdown by the first text column with a small set of values
  const textField = Object.keys(sample).find(k => {
    if (k.startsWith('_')) return false;
    const values = records.map(r => r[k]).filter(v => typeof v === 'string' && v);
    if (values.length < records.length * 0.5) return false;
    const unique = new Set(values);
    return unique.size > 1 && unique.size <= 12;
  });

  if (textField) {
    const counts = new Map<string, number>();
    records.forEach(r => {
      const key = String(r[textField] ?? 'Unknown');
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    charts.push({
      title: `Records by ${textField.replace(/_/g, ' ')}`,
      chart_type: 'pie',
      x_field: textField,
      y_field: 'value',
      aggregation: 'count',
      data: Array.from(counts, ([name, value]) => ({ name, value })),
      config: { runId, runVersion },
    });
  }

  if (!charts.length) return [];

  const rows = charts.map((c, i) => ({
    ...c,
    user_id: userId,
    origin: 'generator',
    source_type: 'workflow',
    source_id: runId,
    source_name: sourceName,
    position: i,
  }));

  const { error } = await supabase.from('saved_charts').insert(rows);
  if (error) throw error;

  return charts.map(c => c.title);
};
